import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import {
    openedStructureDeletionConfirmationDialogState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';

const StructureDeletionConfirmationDialog = ({
    roi,
    isSelected,
}: {
    roi: RoiReadModel;
    isSelected: boolean;
}) => {
    const { structureSet, deleteRoi } = useStructureSet();
    const mainView = useRecoilValue(mainViewState);
    const environment = useRecoilValue(environmentState);
    const selectedTool = useRecoilValue(selectedToolState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const [
        openedStructureDeletionConfirmationDialog,
        setOpenedStructureDeletionConfirmationDialog,
    ] = useRecoilState(openedStructureDeletionConfirmationDialogState);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    useEffect(() => {
        setIsDialogOpen(
            isSelected &&
                openedStructureDeletionConfirmationDialog === roi.roiNumber
        );
    }, [isSelected, openedStructureDeletionConfirmationDialog, roi.roiNumber]);

    const handleDeleteStructureDialogOpen = useCallback(() => {
        setOpenedStructureDeletionConfirmationDialog(roi.roiNumber);
    }, [roi.roiNumber, setOpenedStructureDeletionConfirmationDialog]);

    const quickApplyClearContoursOnSliceOperation = useCallback(() => {
        if (environment && mainView) {
            const planeCoordinates = mainView.position[2];
            environment.clearContourOnSlice(roi.roiNumber, planeCoordinates);
        }
    }, [environment, mainView, roi.roiNumber]);

    useEffect(() => {
        const handleRoiDeletionKeyDown = (event: KeyboardEvent) => {
            const eventSourceElement = event.target as HTMLElement;
            const isEditableElement =
                eventSourceElement.tagName === 'INPUT' ||
                eventSourceElement.tagName === 'TEXTAREA' ||
                (eventSourceElement as HTMLElement).isContentEditable;

            if (isEditableElement) {
                return;
            }
            if (event.key === 'Delete' && isSelected) {
                if (selectedTool === 'brush' || selectedTool === 'line') {
                    quickApplyClearContoursOnSliceOperation();
                } else {
                    if (event.shiftKey) {
                        quickApplyClearContoursOnSliceOperation();
                    } else {
                        handleDeleteStructureDialogOpen();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleRoiDeletionKeyDown);
        return () => {
            document.removeEventListener('keydown', handleRoiDeletionKeyDown);
        };
    }, [
        handleDeleteStructureDialogOpen,
        isSelected,
        quickApplyClearContoursOnSliceOperation,
        selectedTool,
    ]);

    const handleDeleteStructureConfirmation = useCallback(() => {
        if (isSelected && structureSet) {
            setSelectedROI(-1);
            deleteRoi(roi.roiNumber);
            setOpenedStructureDeletionConfirmationDialog(null);
        }
    }, [
        isSelected,
        structureSet,
        deleteRoi,
        roi.roiNumber,
        setSelectedROI,
        setOpenedStructureDeletionConfirmationDialog,
    ]);

    const handleDeleteStructureDialogCancel = () => {
        setOpenedStructureDeletionConfirmationDialog(null);
    };

    const handleConfirmationDialogKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (
                event.key === 'Enter' &&
                openedStructureDeletionConfirmationDialog === roi.roiNumber
            ) {
                handleDeleteStructureConfirmation();
            }
        },
        [
            handleDeleteStructureConfirmation,
            openedStructureDeletionConfirmationDialog,
            roi.roiNumber,
        ]
    );

    return (
        <ConfirmationDialog
            open={isDialogOpen}
            onCancel={handleDeleteStructureDialogCancel}
            onConfirm={handleDeleteStructureConfirmation}
            title={<FormattedMessage id="structureDeletion" />}
            message={
                <FormattedMessage
                    id="structureDeletionConfirmation"
                    values={{
                        structureName: <strong>{roi.name}</strong>,
                    }}
                />
            }
            cancelText={<FormattedMessage id="cancel" />}
            confirmText={<FormattedMessage id="delete" />}
            onKeydown={handleConfirmationDialogKeyDown}
        />
    );
};

export default StructureDeletionConfirmationDialog;
